// AdminAppAtlasPage — Phase 4 of unified back-office.
// 3 tabs (Appellations / Cépages / Producteurs) each with:
//   - Live list (from app.geovinum.io)
//   - "Générer via IA" button opening a modal
//   - After generation, a preview of the fetched fact-sheet JSON
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  MapPin, Grape, Building2, Sparkles, RefreshCw, Loader2, AlertCircle, Search,
  X, Trash2, ExternalLink, Copy, ChevronDown, ChevronRight, Languages,
} from "lucide-react";
import { adminApi } from "@/lib/adminApi";

const TABS = [
  { key: "appellations", label: "Appellations", Icon: MapPin, singular: "appellation" },
  { key: "cepages", label: "Cépages", Icon: Grape, singular: "cépage" },
  { key: "producers", label: "Producteurs", Icon: Building2, singular: "producteur" },
];

// Public path segment on app.geovinum.io for each entity kind.
const APP_PATH_BY_TAB = {
  appellations: "appellations",
  cepages: "cepages",
  producers: "producers",
};
const APP_PUBLIC_BASE = "https://app.geovinum.io";
const publicUrlFor = (tab, item) => {
  const slug = item?.slug;
  if (!slug) return null;
  return `${APP_PUBLIC_BASE}/${APP_PATH_BY_TAB[tab] || tab}/${slug}`;
};

const AdminAppAtlasPage = () => {
  const [tab, setTab] = useState("appellations");
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [preview, setPreview] = useState(null); // generated fact-sheet
  const [expanded, setExpanded] = useState({}); // slug -> bool
  // SEO bulk prewarm state
  const [bulkPrewarm, setBulkPrewarm] = useState(null);
  const [bulkPrewarmError, setBulkPrewarmError] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const fetcher =
        tab === "appellations"
          ? adminApi.listAppAppellations
          : tab === "cepages"
          ? adminApi.listAppCepages
          : adminApi.listAppProducers;
      const res = await fetcher();
      const list = Array.isArray(res) ? res : res?.items || res?.producers || res?.appellations || res?.cepages || [];
      setItems(list);
    } catch (e) {
      setError(e?.message || String(e));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) =>
      (it.name || it.title || "").toLowerCase().includes(q) ||
      (it.slug || "").toLowerCase().includes(q) ||
      (it.region || it.country || "").toLowerCase().includes(q)
    );
  }, [items, query]);

  const currentTab = TABS.find((t) => t.key === tab);
  const Icon = currentTab.Icon;

  const handleDelete = async (item) => {
    if (tab === "producers") return; // producers not editable here
    if (!window.confirm(`Supprimer "${item.name}" ?`)) return;
    try {
      if (tab === "appellations") await adminApi.deleteAppAppellation(item.slug);
      else await adminApi.deleteAppCepage(item.slug);
      setItems((prev) => prev.filter((x) => x.slug !== item.slug));
    } catch (e) {
      setError(e?.message || String(e));
    }
  };

  // SEO — bulk pre-translate every Explorer fiche (appellations + cépages +
  // producteurs) to EN/ES/PT so the whole atlas is indexable in every
  // language. Previously scoped to blog-linked appellations only — now
  // covers the full Explorer graph.
  const handleBulkPrewarm = async () => {
    setBulkPrewarmError(null);
    if (!window.confirm(
      "Lancer la pré-traduction de TOUTES les fiches Explorer (appellations · cépages · producteurs) vers EN/ES/PT ? " +
      "Environ 2 000 paires — tourne en arrière-plan (~10-20 min) et coûte de l'Emergent LLM credit."
    )) return;
    try {
      const res = await adminApi.prewarmAtlasTranslations({
        all_explorer: true,
        kinds: ["appellation", "cepage", "producer"],
        langs: ["en", "es", "pt"],
      });
      if (!res?.ok && res?.detail) { setBulkPrewarmError(res.detail); return; }
      setBulkPrewarm({ running: true, done_pairs: 0, total_pairs: res?.queued_pairs || 0 });
      const tick = async () => {
        try {
          const s = await adminApi.prewarmAtlasStatus();
          setBulkPrewarm(s);
          if (s?.running) setTimeout(tick, 6000);
        } catch (e) { setBulkPrewarmError(e?.message || String(e)); }
      };
      setTimeout(tick, 2000);
    } catch (e) { setBulkPrewarmError(e?.message || String(e)); }
  };

  return (
    <div data-testid="admin-app-atlas">
      <div className="flex items-center justify-between mb-2">
        <h1 className="font-display text-3xl md:text-4xl text-[color:var(--gv-ink)]">Atlas — fiches enrichies</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={handleBulkPrewarm}
            disabled={bulkPrewarm?.running}
            title="Pré-traduire toutes les fiches Explorer (appellations · cépages · producteurs) vers EN/ES/PT"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-[4px] border border-[color:var(--gv-line)] bg-white text-[color:var(--gv-ink)] text-[13px] hover:bg-[color:var(--gv-mist)] disabled:opacity-50"
            data-testid="atlas-bulk-prewarm-btn"
          >
            {bulkPrewarm?.running ? <><Loader2 size={13} className="animate-spin" /> Pré-traduction…</> : <><Languages size={13} /> Pré-traduire Explorer (EN·ES·PT)</>}
          </button>
          <button
            onClick={() => { setPreview(null); setModalOpen(true); }}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[4px] bg-[color:var(--gv-pink)] text-white text-[13px] hover:bg-[color:var(--gv-pink-dark,#C01661)] transition-colors"
            data-testid="atlas-ai-generate-btn"
          >
            <Sparkles size={13} /> Générer via IA
          </button>
        </div>
      </div>
      <p className="text-sm text-[color:var(--gv-ink)]/60 mb-4">Fiches détaillées appellations · cépages · producteurs — génération IA à la demande.</p>

      {(bulkPrewarm || bulkPrewarmError) && (
        <div
          className={`border rounded-[5px] p-3 mb-4 text-[13px] flex items-center gap-2 ${
            bulkPrewarmError ? "bg-rose-50 border-rose-200 text-rose-700" :
            bulkPrewarm?.running ? "bg-amber-50 border-amber-200 text-amber-800" :
            "bg-emerald-50 border-emerald-200 text-emerald-700"
          }`}
          data-testid="atlas-bulk-prewarm-status"
        >
          {bulkPrewarm?.running && <Loader2 size={13} className="animate-spin" />}
          <div className="flex-1">
            {bulkPrewarmError ? (
              <>Erreur : {bulkPrewarmError}</>
            ) : bulkPrewarm?.running ? (
              <>Pré-traduction SEO · {bulkPrewarm.done_pairs || 0} / {bulkPrewarm.total_pairs || 0} · <b>{bulkPrewarm.current || "…"}</b></>
            ) : (
              <>Pré-traduction SEO terminée ✓ {bulkPrewarm?.errors?.length ? `(${bulkPrewarm.errors.length} erreur(s))` : "toutes les fiches indexables en EN/ES/PT."}</>
            )}
          </div>
          {bulkPrewarm && !bulkPrewarm.running && (
            <button onClick={() => { setBulkPrewarm(null); setBulkPrewarmError(null); }} className="text-[11px] px-2 py-1 rounded hover:bg-white/50">✕</button>
          )}
        </div>
      )}

      {/* Bloc Composer hero — entry point to Phase 7 */}
      <div className="bg-gradient-to-br from-[color:var(--gv-pink)]/10 to-[color:var(--gv-pink)]/5 border border-[color:var(--gv-pink)]/30 rounded-[6px] p-4 mb-5 flex items-center justify-between gap-4" data-testid="atlas-blocs-hero">
        <div className="flex items-start gap-3">
          <span className="text-2xl">📚</span>
          <div>
            <div className="font-display text-[15px] text-[color:var(--gv-ink)]">Bloc Composer — 20 catégories × 15+ blocs par fiche</div>
            <div className="text-[12.5px] text-[color:var(--gv-ink)]/65 mt-0.5">
              Génère automatiquement le catalogue pédagogique complet d&apos;une fiche (Histoire, Terroir, Cépages, Anecdotes…). Cliquez sur <b>📚 Blocs</b> à droite d&apos;une ligne pour ouvrir le composeur et lancer la génération.
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-5 border-b border-[color:var(--gv-line)]" data-testid="atlas-tabs">
        {TABS.map((t) => {
          const TIcon = t.Icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => { setTab(t.key); setQuery(""); }}
              className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-[13.5px] border-b-2 transition-colors ${
                active
                  ? "border-[color:var(--gv-pink)] text-[color:var(--gv-pink)] font-medium"
                  : "border-transparent text-[color:var(--gv-ink)]/60 hover:text-[color:var(--gv-ink)]"
              }`}
              data-testid={`atlas-tab-${t.key}`}
            >
              <TIcon size={13} /> {t.label}
            </button>
          );
        })}
      </div>

      {/* Toolbar */}
      <div className="flex gap-2 mb-4 items-center">
        <div className="relative flex-1 max-w-md">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--gv-ink)]/40" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={`Rechercher un ${currentTab.singular}…`}
            className="gv-input w-full pl-8"
            data-testid="atlas-search-input"
          />
        </div>
        <button
          onClick={fetchList}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-[4px] border border-[color:var(--gv-line)] bg-white text-[13px]"
          data-testid="atlas-refresh-btn"
        >
          <RefreshCw size={13} />
        </button>
      </div>

      {error && (
        <div className="bg-rose-50 border border-rose-200 rounded-[5px] p-4 text-sm text-rose-700 flex items-start gap-2 mb-4" data-testid="atlas-error">
          <AlertCircle size={14} className="mt-0.5 shrink-0" />
          <div>
            <div className="font-medium">Impossible de récupérer les {currentTab.label.toLowerCase()}</div>
            <div className="text-[12px] opacity-70 mt-1">Vérifiez que l&apos;app.geovinum.io backend est en ligne.</div>
          </div>
        </div>
      )}

      {/* List */}
      <div className="bg-white border border-[color:var(--gv-line)] rounded-[5px] overflow-hidden">
        {loading ? (
          <div className="p-8 text-sm text-[color:var(--gv-ink)]/55 flex items-center gap-2" data-testid="atlas-loading">
            <Loader2 size={14} className="animate-spin" /> Chargement…
          </div>
        ) : error ? null : filtered.length === 0 ? (
          <div className="p-8 text-sm text-[color:var(--gv-ink)]/55 flex items-center gap-2" data-testid="atlas-empty">
            <Icon size={14} /> {query ? "Aucun résultat" : `Aucun ${currentTab.singular} enregistré`}
          </div>
        ) : (
          <ul>
            {filtered.map((item, idx) => {
              const slug = item.slug || item.id || String(idx);
              const isOpen = !!expanded[slug];
              return (
                <li key={slug} className="border-b border-[color:var(--gv-line)] last:border-b-0" data-testid={`atlas-item-${slug}`}>
                  <div className="flex items-center gap-3 px-4 py-2.5 hover:bg-[color:var(--gv-mist)]/50">
                    <button
                      onClick={() => setExpanded({ ...expanded, [slug]: !isOpen })}
                      className="text-[color:var(--gv-ink)]/50"
                      data-testid={`atlas-expand-${slug}`}
                    >
                      {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-[color:var(--gv-ink)] truncate">{item.name || item.title}</div>
                      <div className="text-[11.5px] text-[color:var(--gv-ink)]/55 truncate">
                        {item.region || item.country || item.country_code || ""} {item.slug ? `· ${item.slug}` : ""}
                      </div>
                    </div>
                    {tab !== "producers" && (
                      <button
                        onClick={() => handleDelete(item)}
                        className="p-1.5 rounded hover:bg-red-50 text-[color:var(--gv-ink)]/40 hover:text-red-600"
                        title="Supprimer"
                        data-testid={`atlas-delete-${slug}`}
                      >
                        <Trash2 size={13} />
                      </button>
                    )}
                    {publicUrlFor(tab, item) && (
                      <a
                        href={publicUrlFor(tab, item)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1.5 rounded hover:bg-[color:var(--gv-mist)] text-[color:var(--gv-ink)]/50 hover:text-[color:var(--gv-pink)]"
                        title="Voir sur app.geovinum.io"
                        data-testid={`atlas-view-${slug}`}
                      >
                        <ExternalLink size={13} />
                      </a>
                    )}
                    {item.slug && (
                      <Link
                        to={`/admin/atlas-blocks/${tab}/${item.slug}`}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-[4px] bg-[color:var(--gv-pink)]/10 border border-[color:var(--gv-pink)]/30 text-[color:var(--gv-pink)] text-[12px] font-medium hover:bg-[color:var(--gv-pink)] hover:text-white transition-colors"
                        title="Composer les blocs (20 catégories pédagogiques) — accès au bouton Générer tout"
                        data-testid={`atlas-blocks-link-${slug}`}
                      >
                        📚 Blocs IA
                      </Link>
                    )}
                    <button
                      onClick={() => {
                        setPreview({ __seed_from: item, __tab: tab, ...item });
                        setModalOpen(true);
                      }}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-[4px] border border-[color:var(--gv-line)] text-[12px] hover:bg-[color:var(--gv-mist)]"
                      title="Régénérer via IA"
                      data-testid={`atlas-regenerate-${slug}`}
                    >
                      <Sparkles size={11} /> IA
                    </button>
                  </div>
                  {isOpen && (
                    <div className="px-4 pb-4 pt-1 bg-[color:var(--gv-mist)]/30">
                      <FactSheetJson data={item} />
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <div className="text-[11.5px] text-[color:var(--gv-ink)]/50 mt-2">{filtered.length} sur {items.length}</div>

      {modalOpen && (
        <GenerateModal
          tab={tab}
          seed={preview?.__seed_from}
          initialSeed={preview}
          onClose={() => { setModalOpen(false); setPreview(null); }}
          onGenerated={(sheet) => { setPreview(sheet); }}
          onSaved={() => { fetchList(); }}
        />
      )}
    </div>
  );
};

const GenerateModal = ({ tab, seed, initialSeed, onClose, onGenerated, onSaved }) => {
  const [name, setName] = useState(seed?.name || "");
  const [region, setRegion] = useState(seed?.region || "");
  const [country, setCountry] = useState(seed?.country || "France");
  const [website, setWebsite] = useState(seed?.website || "");
  const [extra, setExtra] = useState("");
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [savedItem, setSavedItem] = useState(null);
  const [err, setErr] = useState(null);
  const [sheet, setSheet] = useState(initialSeed && !initialSeed.__seed_from ? initialSeed : null);
  const [copied, setCopied] = useState(false);

  const handleGenerate = async () => {
    if (!name.trim()) { setErr("Nom requis"); return; }
    setBusy(true);
    setErr(null);
    setSaved(false);
    try {
      let result;
      if (tab === "appellations") {
        result = await adminApi.aiGenerateAppellation({ name, region, country, extra_instructions: extra });
      } else if (tab === "cepages") {
        result = await adminApi.aiGenerateCepage({ name, extra_instructions: extra });
      } else {
        result = await adminApi.aiGenerateProducer({ name, website, region, extra_instructions: extra });
      }
      setSheet(result);
      onGenerated?.(result);
    } catch (e) {
      setErr(e?.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  const handleSave = async () => {
    if (!sheet) return;
    setSaving(true);
    setErr(null);
    try {
      // Strip internal seed markers before sending to upstream.
      const payload = { ...sheet };
      delete payload.__seed_from;
      delete payload.__tab;
      let saved_item;
      if (tab === "appellations") {
        saved_item = await adminApi.upsertAppAppellation(payload);
      } else if (tab === "cepages") {
        saved_item = await adminApi.upsertAppCepage(payload);
      } else {
        saved_item = await adminApi.upsertAppProducer(payload);
      }
      setSaved(true);
      setSavedItem(saved_item);
      onSaved?.(saved_item);
      // Keep modal open — admin may want to click "Voir en ligne" to inspect.
    } catch (e) {
      // Log full error to console so admins can inspect via DevTools when
      // the message alone isn't enough (e.g. Pydantic loc paths).
      console.error("[atlas save error]", { status: e?.status, message: e?.message, data: e?.data });
      const raw = e?.data?.detail;
      const detail =
        typeof raw === "string" ? raw :
        Array.isArray(raw) ? raw.map((x) => `${(x?.loc || []).join(".")} : ${x?.msg || ""}`).join(" · ") :
        e?.message || String(e);
      setErr(`Échec de l'enregistrement : ${detail}`);
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = async () => {
    if (!sheet) return;
    await navigator.clipboard.writeText(JSON.stringify(sheet, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };

  const title = tab === "appellations" ? "Générer une fiche appellation"
    : tab === "cepages" ? "Générer une fiche cépage"
    : "Générer une fiche producteur";

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-[8px] w-full max-w-2xl max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid={`atlas-generate-modal-${tab}`}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-[color:var(--gv-line)]">
          <div className="flex items-center gap-2">
            <Sparkles size={16} className="text-[color:var(--gv-pink)]" />
            <h3 className="font-display text-xl text-[color:var(--gv-ink)]">{title}</h3>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-[color:var(--gv-mist)]"><X size={16} /></button>
        </div>

        <div className="overflow-auto p-6 flex-1">
          <label className="block text-[11.5px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 mb-1">Nom {tab === "appellations" ? "de l'appellation" : tab === "cepages" ? "du cépage" : "du producteur"} *</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={tab === "appellations" ? "Ex : Chablis" : tab === "cepages" ? "Ex : Chardonnay" : "Ex : Domaine Leflaive"}
            className="gv-input w-full mb-3"
            data-testid="atlas-gen-name"
          />

          {tab === "appellations" && (
            <>
              <label className="block text-[11.5px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 mb-1">Région</label>
              <input value={region} onChange={(e) => setRegion(e.target.value)} placeholder="Ex : Bourgogne" className="gv-input w-full mb-3" data-testid="atlas-gen-region" />
              <label className="block text-[11.5px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 mb-1">Pays</label>
              <input value={country} onChange={(e) => setCountry(e.target.value)} className="gv-input w-full mb-3" data-testid="atlas-gen-country" />
            </>
          )}

          {tab === "producers" && (
            <>
              <label className="block text-[11.5px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 mb-1">Site web (optionnel)</label>
              <input value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://…" className="gv-input w-full mb-3" data-testid="atlas-gen-website" />
              <label className="block text-[11.5px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 mb-1">Région / appellation principale</label>
              <input value={region} onChange={(e) => setRegion(e.target.value)} className="gv-input w-full mb-3" data-testid="atlas-gen-region" />
            </>
          )}

          <label className="block text-[11.5px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 mb-1">Instructions complémentaires</label>
          <textarea
            value={extra}
            onChange={(e) => setExtra(e.target.value)}
            rows={3}
            placeholder="Ex : Insister sur l'histoire biodynamique du domaine, ou sur les vins de garde…"
            className="gv-input w-full mb-3 resize-none"
            data-testid="atlas-gen-extra"
          />

          {err && <div className="text-[12px] text-red-600 mb-3" data-testid="atlas-gen-error">{err}</div>}

          {sheet && (
            <div className="mt-2 border border-[color:var(--gv-line)] rounded-[5px] overflow-hidden" data-testid="atlas-gen-preview">
              <div className="flex items-center justify-between px-3 py-2 bg-[color:var(--gv-mist)] border-b border-[color:var(--gv-line)]">
                <span className="text-[11.5px] uppercase tracking-widest text-[color:var(--gv-ink)]/60">Fiche générée — éditable</span>
                <button onClick={handleCopy} className="text-[11.5px] inline-flex items-center gap-1 text-[color:var(--gv-ink)]/70 hover:text-[color:var(--gv-pink)]" data-testid="atlas-gen-copy">
                  <Copy size={11} /> {copied ? "Copié !" : "Copier JSON"}
                </button>
              </div>
              <FactSheetEditor data={sheet} onChange={setSheet} />
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 px-6 py-3 border-t border-[color:var(--gv-line)] bg-[color:var(--gv-mist)]/50">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-[4px] border border-[color:var(--gv-line)] text-[13px]"
            data-testid="atlas-gen-close"
          >
            Fermer
          </button>
          <button
            onClick={handleGenerate}
            disabled={busy || saving || !name.trim()}
            className="px-4 py-2 rounded-[4px] border border-[color:var(--gv-pink)] text-[color:var(--gv-pink)] bg-white text-[13px] disabled:opacity-50 inline-flex items-center gap-1.5"
            data-testid="atlas-gen-submit"
          >
            {busy ? <><Loader2 size={13} className="animate-spin" /> Génération 30-45 s…</> : <><Sparkles size={13} /> {sheet ? "Régénérer" : "Générer"}</>}
          </button>
          {sheet && tab === "producers" && (
            <div className="text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2.5 py-1.5 mr-auto max-w-md" data-testid="atlas-gen-producers-notice">
              L&apos;enregistrement des fiches producteurs sera activé dès qu&apos;app.geovinum.io expose l&apos;endpoint dédié. Pour l&apos;instant, copiez le JSON.
            </div>
          )}
          {sheet && tab !== "producers" && (
            <button
              onClick={handleSave}
              disabled={busy || saving}
              className={`px-4 py-2 rounded-[4px] text-white text-[13px] disabled:opacity-50 inline-flex items-center gap-1.5 ${
                saved ? "bg-emerald-600" : "bg-[color:var(--gv-pink)] hover:bg-[color:var(--gv-pink-dark,#C01661)]"
              }`}
              data-testid="atlas-gen-save"
            >
              {saving ? (
                <><Loader2 size={13} className="animate-spin" /> Enregistrement…</>
              ) : saved ? (
                <>✓ Enregistré</>
              ) : (
                <>Enregistrer la fiche</>
              )}
            </button>
          )}
          {saved && savedItem && publicUrlFor(tab, savedItem) && (
            <a
              href={publicUrlFor(tab, savedItem)}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 rounded-[4px] border border-[color:var(--gv-line)] bg-white text-[13px] inline-flex items-center gap-1.5 hover:bg-[color:var(--gv-mist)]"
              data-testid="atlas-gen-view-public"
            >
              <ExternalLink size={13} /> Voir en ligne
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

const FactSheetJson = ({ data }) => {
  const clean = { ...data };
  delete clean.__seed_from; delete clean.__tab;
  const entries = Object.entries(clean).filter(([k]) => !k.startsWith("_"));
  return (
    <div className="text-[12.5px] text-[color:var(--gv-ink)]/85 font-mono leading-relaxed max-h-[40vh] overflow-auto p-3">
      {entries.map(([k, v]) => (
        <div key={k} className="mb-1">
          <span className="text-[color:var(--gv-pink)]">{k}</span>
          <span className="text-[color:var(--gv-ink)]/50"> : </span>
          <span>{typeof v === "object" ? JSON.stringify(v, null, 2) : String(v)}</span>
        </div>
      ))}
    </div>
  );
};

// Fields treated as short single-line inputs. Everything else that is a
// string longer than 120 chars becomes a textarea automatically.
const SHORT_FIELDS = new Set([
  "id", "slug", "name", "region", "sub_region", "country", "country_code",
  "type", "classification", "appellation_type",
  "color", "origin", "website", "regulation_year",
  "hex", "iso_code", "grape_color", "climate", "yield", "yield_max_hl_ha",
  "area_ha", "altitude", "altitude_min_m", "altitude_max_m",
  "elevation_min_m", "elevation_max_m", "producers_count", "annual_production",
  "planting_density", "min_alcohol", "rainfall_mm", "avg_temperature_c",
  "aging_min_years", "aging_max_years", "service_temp_ideal_c",
  "open_ahead_min", "founded_year",
  "created_at", "updated_at",
]);

// Order-of-display for well-known fields (rest appended alphabetically).
// Ordering follows the 19-section pedagogical spec.
const FIELD_ORDER = [
  // §1 · Carte d'identité
  "name", "slug", "country", "region", "sub_region",
  "classification", "type", "appellation_type", "regulation_year",
  "area_ha", "altitude", "altitude_min_m", "altitude_max_m",
  "producers_count", "annual_production", "wine_colors",
  // §2 · Situation géographique
  "location", "communes", "map_coords", "map_url",
  "climate", "relief_influence", "water_proximity",
  // §3 · Histoire
  "history_origin", "history_evolution", "history_key_figures",
  "history_events", "official_recognition",
  "history", "history_fr", "history_en", "history_es", "history_pt",
  // §4 · Géologie et terroir
  "soil_types", "subsoil_types", "geological_age", "exposure", "slope",
  "drainage", "rainfall_mm", "avg_temperature_c", "climate_risks",
  "geology", "terroir_details",
  "terroir_fr", "terroir_en", "terroir_es", "terroir_pt", "terroir",
  // §5 · Cépages
  "grapes_detail", "authorized_grapes",
  // §6 · Règles de production
  "yield_max_hl_ha", "planting_density", "pruning",
  "irrigation_allowed", "harvest_type", "min_aging", "min_alcohol",
  "specific_constraints",
  "regulation_fr", "regulation_en", "regulation_es", "regulation_pt", "regulation",
  // §7 · Profil des vins
  "wine_profile_red", "wine_profile_white", "wine_profile_rose",
  // §8 · Profil aromatique
  "aromatic_wheel", "aromas", "descriptors",
  // §9 · Échelle sensorielle
  "sensory_scale",
  // §10 · Température de service
  "service_temp",
  // §11 · Potentiel de garde
  "aging_potential", "aging_range", "aging_min_years", "aging_max_years",
  // §12 · Accords mets & vins
  "food_pairings", "food_pairings_detail",
  // §13 · Millésimes
  "vintages",
  // §14 · Climats / Crus
  "premiers_crus", "grands_crus", "famous_climats", "lieux_dits",
  "lieux_dits_premiers_crus",
  // §15 · Producteurs
  "producers_detail", "producers",
  // §16 · Chiffres clés
  "kpis",
  // §17 · Anecdotes
  "anecdotes", "fun_facts",
  // §18 · Tourisme
  "tourism",
  // §19 · Appellations proches
  "similar_appellations", "regions",
  // Descriptions multilingues (bas de fiche)
  "description_fr", "description_en", "description_es", "description_pt", "description",
];

const FIELD_LABELS = {
  // §1 · Carte d'identité
  name: "Nom", slug: "Slug",
  country: "Pays", region: "Région viticole", sub_region: "Sous-région",
  classification: "Classification (AOC · DOC · DOCG · AVA…)",
  type: "Type", appellation_type: "Type d'appellation (Village · 1er Cru · Grand Cru)",
  regulation_year: "Année de création",
  area_ha: "Superficie (ha)", altitude: "Altitude",
  altitude_min_m: "Altitude min (m)", altitude_max_m: "Altitude max (m)",
  elevation_min_m: "Altitude min (m)", elevation_max_m: "Altitude max (m)",
  producers_count: "Nb de producteurs (approx.)",
  annual_production: "Production annuelle",
  wine_colors: "Couleurs produites",
  // §2 · Situation géographique
  location: "Localisation précise", communes: "Communes concernées",
  map_coords: "Coordonnées carte", map_url: "URL carte",
  climate: "Climat", relief_influence: "Influence des reliefs",
  water_proximity: "Proximité des cours d'eau",
  // §3 · Histoire
  history_origin: "Origine de l'appellation",
  history_evolution: "Évolution historique",
  history_key_figures: "Personnages importants",
  history_events: "Événements marquants",
  official_recognition: "Reconnaissance officielle",
  history: "Histoire",
  history_fr: "Histoire (FR)", history_en: "Histoire (EN)",
  history_es: "Histoire (ES)", history_pt: "Histoire (PT)",
  // §4 · Géologie et terroir
  soil_types: "Types de sols", subsoil_types: "Sous-sols",
  geological_age: "Âge géologique", exposure: "Exposition", slope: "Pente",
  drainage: "Drainage", rainfall_mm: "Pluviométrie (mm/an)",
  avg_temperature_c: "Température moyenne (°C)",
  climate_risks: "Risques climatiques",
  geology: "Géologie", terroir_details: "Détails du terroir",
  terroir: "Terroir",
  terroir_fr: "Terroir (FR)", terroir_en: "Terroir (EN)",
  terroir_es: "Terroir (ES)", terroir_pt: "Terroir (PT)",
  // §5 · Cépages
  grapes_detail: "Cépages détaillés (nom · couleur · % · rôle · arômes · garde)",
  authorized_grapes: "Cépages autorisés (liste simple)",
  // §6 · Règles de production
  yield_max_hl_ha: "Rendement max (hl/ha)",
  planting_density: "Densité de plantation (pieds/ha)",
  pruning: "Taille de la vigne",
  irrigation_allowed: "Irrigation autorisée",
  harvest_type: "Vendanges (manuelles/mécaniques)",
  min_aging: "Élevage minimum",
  min_alcohol: "Degré alcoolique min.",
  specific_constraints: "Contraintes spécifiques du cahier des charges",
  regulation: "Cahier des charges",
  regulation_fr: "Cahier des charges (FR)", regulation_en: "Cahier des charges (EN)",
  regulation_es: "Cahier des charges (ES)", regulation_pt: "Cahier des charges (PT)",
  // §7 · Profil des vins
  wine_profile_red: "Profil des rouges (robe · nez · bouche · structure · tanins · acidité · longueur)",
  wine_profile_white: "Profil des blancs (fraîcheur · gras · minéralité · tension · complexité)",
  wine_profile_rose: "Profil des rosés",
  // §8 · Profil aromatique
  aromatic_wheel: "Profil aromatique (roue simplifiée)",
  aromas: "Arômes", descriptors: "Descripteurs",
  // §9 · Échelle sensorielle
  sensory_scale: "Échelle sensorielle (0-5)",
  // §10 · Température de service
  service_temp: "Température de service · carafage · ouverture",
  // §11 · Potentiel de garde
  aging_potential: "Potentiel de garde",
  aging_range: "Fourchette de garde",
  aging_min_years: "Garde min (années)",
  aging_max_years: "Garde max (années)",
  // §12 · Accords mets & vins
  food_pairings: "Accords mets & vins (liste)",
  food_pairings_detail: "Accords mets & vins détaillés (par catégorie)",
  // §13 · Millésimes
  vintages: "Millésimes (très grands · grands · accessibles · récents)",
  // §14 · Climats / Crus
  premiers_crus: "Premiers Crus", grands_crus: "Grands Crus",
  famous_climats: "Climats célèbres", lieux_dits: "Lieux-dits",
  lieux_dits_premiers_crus: "Lieux-dits Premiers Crus",
  // §15 · Producteurs
  producers_detail: "Producteurs détaillés (nom · spécialité · surface · certification)",
  producers: "Producteurs emblématiques",
  // §16 · Chiffres clés
  kpis: "Chiffres clés",
  // §17 · Anecdotes
  anecdotes: "Anecdotes · Le saviez-vous ?",
  fun_facts: "Le saviez-vous ?",
  // §18 · Tourisme
  tourism: "Tourisme (route · caves · musées · UNESCO · événements)",
  // §19 · Appellations proches
  similar_appellations: "Appellations proches (à découvrir)",
  regions: "Régions",
  // Descriptions multilingues
  description: "Description (défaut)",
  description_fr: "Description (FR)", description_en: "Description (EN)",
  description_es: "Description (ES)", description_pt: "Description (PT)",
  // Divers cépages
  origin: "Origine", website: "Site web", country_code: "Code pays",
  color: "Couleur", grape_color: "Couleur du cépage",
  founded_year: "Année de fondation",
};

const HIDDEN_FIELDS = new Set(["id", "created_at", "updated_at"]);

// Editable version of the fact-sheet — every field becomes an input,
// textarea, or chip-list depending on its type/length.
const FactSheetEditor = ({ data, onChange }) => {
  const clean = { ...data };
  delete clean.__seed_from; delete clean.__tab;
  const keys = Object.keys(clean).filter((k) => !k.startsWith("_") && !HIDDEN_FIELDS.has(k));
  // Sort: known fields first (in FIELD_ORDER), rest alphabetically.
  keys.sort((a, b) => {
    const ia = FIELD_ORDER.indexOf(a);
    const ib = FIELD_ORDER.indexOf(b);
    if (ia !== -1 && ib !== -1) return ia - ib;
    if (ia !== -1) return -1;
    if (ib !== -1) return 1;
    return a.localeCompare(b);
  });

  const patch = (field, value) => {
    onChange({ ...data, [field]: value });
  };

  return (
    <div className="max-h-[52vh] overflow-auto p-3 space-y-3 bg-white" data-testid="atlas-editor">
      {keys.map((k) => (
        <FieldRow key={k} field={k} value={clean[k]} onChange={(v) => patch(k, v)} />
      ))}
    </div>
  );
};

const FieldRow = ({ field, value, onChange }) => {
  const label = FIELD_LABELS[field] || field;
  const isArray = Array.isArray(value);
  const isObject = !isArray && value !== null && typeof value === "object";
  const isShort = SHORT_FIELDS.has(field) || (typeof value === "string" && value.length <= 120);

  return (
    <div>
      <label className="block text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 mb-1">{label}</label>
      {isArray ? (
        <ChipEditor items={value} onChange={onChange} testid={`atlas-editor-${field}`} />
      ) : isObject ? (
        <textarea
          value={JSON.stringify(value, null, 2)}
          onChange={(e) => {
            try { onChange(JSON.parse(e.target.value)); } catch { /* keep raw text until valid */ }
          }}
          rows={4}
          className="gv-input w-full font-mono text-[11.5px] resize-y"
          data-testid={`atlas-editor-${field}`}
        />
      ) : isShort ? (
        <input
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          className="gv-input w-full text-[13px]"
          data-testid={`atlas-editor-${field}`}
        />
      ) : (
        <textarea
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          rows={Math.min(10, Math.max(3, Math.ceil((value?.length || 0) / 90)))}
          className="gv-input w-full text-[13px] resize-y leading-relaxed"
          data-testid={`atlas-editor-${field}`}
        />
      )}
    </div>
  );
};

const ChipEditor = ({ items, onChange, testid }) => {
  const [draft, setDraft] = useState("");
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    if (items.includes(v)) { setDraft(""); return; }
    onChange([...items, v]);
    setDraft("");
  };
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  return (
    <div className="border border-[color:var(--gv-line)] rounded-[4px] p-2 bg-white" data-testid={testid}>
      <div className="flex flex-wrap gap-1.5 mb-1.5">
        {items.map((it, i) => (
          <span key={i} className="inline-flex items-center gap-1 pl-2.5 pr-1 py-0.5 rounded-full bg-[color:var(--gv-mist)] text-[12px] border border-[color:var(--gv-line)]">
            {String(it)}
            <button
              onClick={() => remove(i)}
              className="p-0.5 rounded hover:bg-red-100 text-[color:var(--gv-ink)]/50 hover:text-red-600"
              title="Retirer"
              data-testid={`${testid}-remove-${i}`}
            >
              <X size={10} />
            </button>
          </span>
        ))}
        {items.length === 0 && (
          <span className="text-[11.5px] text-[color:var(--gv-ink)]/40 italic">Aucun élément</span>
        )}
      </div>
      <div className="flex gap-1.5">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder="Ajouter et Entrée…"
          className="gv-input flex-1 text-[12.5px] py-1"
          data-testid={`${testid}-input`}
        />
        <button
          onClick={add}
          disabled={!draft.trim()}
          className="px-2.5 py-1 rounded-[4px] border border-[color:var(--gv-line)] text-[12px] disabled:opacity-40"
          data-testid={`${testid}-add`}
        >
          +
        </button>
      </div>
    </div>
  );
};

export default AdminAppAtlasPage;
